/**
 * The pending credit purchase's `request_id`, kept in the tab's `sessionStorage` until its outcome is known
 * (B.4, OR-17, C17; audit A25a), so Buy after a close, a reload or a remount replays the same purchase instead
 * of charging a second block. The purchase mutation settles it, which still runs after the component that
 * started the purchase unmounted. Storage that throws (private mode, quota) falls back to component state.
 */
import { ApiError } from '@/lib/api'

export const PURCHASE_REQUEST_KEY = 'trellis.purchaseRequest'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The id an opening sends: the stored pending one, or a new one when storage holds none (never set, or settled
 * after this component read it, Bugbot on PR 56). Component state, `current`, decides only when storage throws.
 */
export function pendingPurchaseOr(current: string | null): string {
  try {
    const value = window.sessionStorage.getItem(PURCHASE_REQUEST_KEY)
    return value !== null && UUID.test(value) ? value : crypto.randomUUID()
  } catch {
    return current ?? crypto.randomUUID()
  }
}

export function storePendingPurchase(id: string): void {
  try {
    window.sessionStorage.setItem(PURCHASE_REQUEST_KEY, id)
  } catch {
    // Storage refused: the id still lives in component state for this mount.
  }
}

/** A purchase with a known outcome: its id is cleared, unless a newer one has replaced it. */
export function settlePendingPurchase(id: string): void {
  try {
    if (window.sessionStorage.getItem(PURCHASE_REQUEST_KEY) === id) window.sessionStorage.removeItem(PURCHASE_REQUEST_KEY)
  } catch {
    // Storage refused: nothing was stored.
  }
}

/** A definite refusal (4xx): nothing was charged under this id, so the next purchase draws a new one. */
export function isDefiniteRefusal(error: unknown): boolean {
  return error instanceof ApiError && error.problem.status >= 400 && error.problem.status < 500
}
